import React, { useMemo } from 'react';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  ReportBody,
  ReportField,
  ReportFooter,
  ReportHeader,
  ReportMeta,
  ReportMetaRow,
  ReportNote,
  ReportPack,
  ReportPage,
  ReportSignLine,
  ReportSigns,
  ReportTitleRow,
} from '../../../components/ui/Report';
import type { CrewVesselListResponse, CrewVesselListRow } from '../types';

function dash(v: React.ReactNode) {
  if (v == null || v === '') return '—';
  return v;
}

function fmtDate(v?: string | null) {
  return v ? formatDateGlobal(v) : '—';
}

function fullName(row: CrewVesselListRow): string {
  return [row.lastName, row.firstName, row.middleName].filter(Boolean).join(' ').trim() || '—';
}

function fmtWage(row: CrewVesselListRow): string {
  if (row.wage == null) return '—';
  const amount = typeof row.wage === 'number' ? row.wage : Number(row.wage);
  if (!Number.isFinite(amount)) return String(row.wage);
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return row.wageCurrency ? `${formatted} ${row.wageCurrency}` : formatted;
}

export type CrewWagesListReportProps = {
  data: CrewVesselListResponse;
  companyLogoSrc?: string | null;
  companyName?: string | null;
};

export const CrewWagesListReport: React.FC<CrewWagesListReportProps> = ({
  data,
  companyLogoSrc = null,
  companyName = null,
}) => {
  const vessel = data.vessel;
  const vesselName = vessel?.vesselName || '—';
  const periodLabel =
    data.periodFrom && data.periodTo && data.periodFrom !== data.periodTo
      ? `${fmtDate(data.periodFrom)} – ${fmtDate(data.periodTo)}`
      : fmtDate(data.periodFrom || data.periodTo);
  const rows = data.rows ?? [];

  const wageSummary = useMemo(() => {
    const byCcy = new Map<string, number>();
    for (const row of rows) {
      if (row.wage == null) continue;
      const amount = typeof row.wage === 'number' ? row.wage : Number(row.wage);
      if (!Number.isFinite(amount)) continue;
      const ccy = (row.wageCurrency || '—').toUpperCase();
      byCcy.set(ccy, (byCcy.get(ccy) ?? 0) + amount);
    }
    return Array.from(byCcy.entries())
      .map(([ccy, total]) => {
        const formatted = total.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        return ccy === '—' ? formatted : `${formatted} ${ccy}`;
      })
      .join(' · ');
  }, [rows]);

  return (
    <ReportPack className="crew-list-print-report crew-list-print-wages">
      <ReportPage size="portrait">
        <ReportHeader>
          <ReportTitleRow
            title="Crew Wages & Contracts"
            formId="Manning · wages"
            logoSrc={companyLogoSrc}
            logoAlt={companyName || 'Management company logo'}
          />
          <ReportMeta>
            <ReportMetaRow>
              <ReportField label="Vessel" value={vesselName} />
              <ReportField label="IMO number" value={dash(vessel?.imoNumber)} />
              <ReportField label="Period" value={periodLabel} />
            </ReportMetaRow>
            <ReportMetaRow>
              <ReportField label="Crew count" value={String(rows.length)} />
              <ReportField label="Total wages" value={wageSummary || '—'} colSpan={2} />
            </ReportMetaRow>
          </ReportMeta>
        </ReportHeader>

        <ReportBody>
          <table className="crew-list-print-table">
            <thead>
              <tr>
                <th className="crew-list-print-col-no">No.</th>
                <th>Rank</th>
                <th>Name</th>
                <th>Wage</th>
                <th>Sign-on</th>
                <th>Intention S/Off</th>
                <th>COE</th>
                <th>Latest / Ext.</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="crew-list-print-empty">
                    No crew assigned for this vessel and period.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row.transactionId}>
                    <td className="crew-list-print-col-no">{i + 1}</td>
                    <td>{dash(row.lastRank)}</td>
                    <td>{fullName(row)}</td>
                    <td className="crew-list-print-col-wage">{fmtWage(row)}</td>
                    <td>{fmtDate(row.signOnDate)}</td>
                    <td>{fmtDate(row.intentionSignOffDate)}</td>
                    <td>{fmtDate(row.coeDate)}</td>
                    <td>
                      {row.latest || row.extCoeDate
                        ? [row.latest ? fmtDate(row.latest) : null, row.extCoeDate ? `Ext ${fmtDate(row.extCoeDate)}` : null]
                            .filter(Boolean)
                            .join(' · ')
                        : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportBody>

        <ReportFooter>
          <ReportSigns>
            <ReportSignLine label="Prepared by" />
            <ReportSignLine label="Approved by" />
          </ReportSigns>
          {companyName ? <ReportNote>Issued by {companyName}</ReportNote> : null}
        </ReportFooter>
      </ReportPage>
    </ReportPack>
  );
};

export default CrewWagesListReport;
